<template>
  <teleport to="body">
    <transition name="mobile-sidebar">
      <div
        v-if="isOpen"
        class="mobile-sidebar-overlay"
        @click.self="close"
        @touchmove.prevent
        @wheel.prevent
      >
        <!-- ── Main sidebar ── -->
        <aside class="mobile-sidebar" @click.stop @touchmove.stop @wheel.stop>
          <!-- Bot selector -->
          <div v-if="hasBots" class="mobile-sidebar__bot-selector">
            <div class="bot-selector-current" @click="botDropdownOpen = !botDropdownOpen">
              <img
                v-if="currentBot"
                :src="'https://t.me/i/userpic/320/' + userBots[currentBot.key].bot_name + '.jpg'"
                alt=""
                class="bot-avatar"
                @error="$event.target.src = noAvatarImg"
              />
              <span class="bot-name">{{ currentBot ? userBots[currentBot.key].bot_name : '' }}</span>
              <el-tooltip v-if="currentBot && !userBots[currentBot.key].status" :content="$t('General.Bots.BotNotConnected')" raw-content>
                <i class="fas fa-exclamation-triangle text-warning ms-1"></i>
              </el-tooltip>
              <i class="fas fa-chevron-down bot-caret" :class="{ 'bot-caret--open': botDropdownOpen }"></i>
            </div>
            <transition name="bot-dropdown">
              <div v-if="botDropdownOpen" class="bot-dropdown">
                <div
                  v-for="bot in userBots"
                  :key="bot.id"
                  class="bot-dropdown__item"
                  @click="switchBot(bot)"
                >
                  <img
                    :src="'https://t.me/i/userpic/320/' + bot.bot_name + '.jpg'"
                    alt=""
                    class="bot-avatar bot-avatar--sm"
                    @error="$event.target.src = noAvatarImg"
                  />
                  <span>{{ bot.bot_name }}</span>
                  <i v-if="!bot.status" class="fas fa-exclamation-triangle text-warning ms-auto"></i>
                </div>
              </div>
            </transition>
          </div>
          <div v-else class="mobile-sidebar__bot-selector">
            <div class="bot-selector-current" @click="navigateTo('/resorces/connect')">
              <i class="fas fa-plus-circle me-2"></i>
              <span>{{ $t('General.Bots.AddResource') }}</span>
            </div>
          </div>

          <div class="mobile-sidebar__divider"></div>

          <!-- 2FA promo -->
          <div v-if="show2FAPromo" class="mobile-sidebar__2fa-promo" @click="goTo2FA">
            <i class="fas fa-shield-alt me-2"></i>
            {{ $t('Navbar.Protect2FA') }}
          </div>

          <!-- Menu items -->
          <nav class="mobile-sidebar__nav">

            <!-- ── ОБЗОР ── -->
            <div class="nav-section-header">{{ $t('Menu.SectionOverview') }}</div>

            <div class="nav-item" @click="navigateTo('/')">
              <i class="fas fa-th-large nav-icon"></i>
              <span>{{ $t('Menu.Dashboard') }}</span>
            </div>

            <div class="nav-item" :class="{ 'nav-item--premium': isFreeTariff }" @click="navigateTo('/dashboards')">
              <i class="fas fa-th-large nav-icon"></i>
              <span>{{ $t('Menu.Dashboards') }}</span>
            </div>

            <div class="nav-item" :class="{ 'nav-item--premium': isFreeTariff }" @click="navigateTo('/users')">
              <i class="fas fa-users nav-icon"></i>
              <span>{{ $t('Menu.Users') }}</span>
            </div>

            <!-- ── ОТЧЁТЫ ── -->
            <div class="nav-section-header">{{ $t('Menu.SectionReports') }}</div>

            <!-- Reports → opens sub-panel -->
            <div class="nav-item nav-item--has-panel" @click="openReportsPanel">
              <i class="fas fa-chart-line nav-icon"></i>
              <span>{{ $t('Menu.Reports') }}</span>
              <i class="fas fa-chevron-right nav-arrow"></i>
            </div>

            <div class="nav-item" :class="{ 'nav-item--premium': isFreeTariff }" @click="navigateTo('/cohorts')">
              <i class="fas fa-users nav-icon"></i>
              <span>{{ $t('Menu.Cohorts') }}</span>
            </div>

            <!-- Conversions (accordion) -->
            <div class="nav-item nav-item--accordion" :class="{ 'nav-item--premium': isFreeTariff }" @click="toggleAccordion('conversions')">
              <i class="fas fa-percentage nav-icon"></i>
              <span>{{ $t('Menu.Conversions') }}</span>
              <i class="fas fa-chevron-down nav-caret" :class="{ 'nav-caret--open': openAccordions.conversions }"></i>
            </div>
            <transition name="accordion">
              <div v-if="openAccordions.conversions" class="nav-sub">
                <div class="nav-sub-item" @click="navigateTo('/conversion')">{{ $t('Menu.ConversionsList') }}</div>
                <div class="nav-sub-item" @click="navigateTo('/reports/conversion_summary')">{{ $t('Menu.ConversionsSummary') }}</div>
                <div class="nav-sub-item" @click="navigateToNamed('Targets')">{{ $t('Menu.TargetSettings') }}</div>
                <div class="nav-sub-item" @click="navigateToNamed('EventRules')">{{ $t('Menu.EventRules') }}</div>
              </div>
            </transition>

            <!-- Reports old (accordion) -->
            <div class="nav-item nav-item--accordion" @click="toggleAccordion('reports_old')">
              <i class="fas fa-chart-line nav-icon"></i>
              <span>{{ $t('Menu.Reports') }}</span>
              <el-tag type="info" effect="dark" size="small" class="ms-1" style="height:16px;line-height:16px;padding:0 4px;font-size:10px;border:none;flex-shrink:0;">old</el-tag>
              <i class="fas fa-chevron-down nav-caret" :class="{ 'nav-caret--open': openAccordions.reports_old }"></i>
            </div>
            <transition name="accordion">
              <div v-if="openAccordions.reports_old" class="nav-sub">
                <!-- Behaviour -->
                <div class="nav-sub-header">{{ $t('Menu.Behaviour') }}</div>
                <div v-if="!isChannel" class="nav-sub-item" @click="navigateTo('/reports/active_users?details=month')">MAU</div>
                <div v-if="!isChannel" class="nav-sub-item" @click="navigateTo('/reports/active_users?details=week')">WAU</div>
                <div v-if="!isChannel" class="nav-sub-item" @click="navigateTo('/reports/active_users?details=day')">DAU</div>
                <div class="nav-sub-item" @click="navigateTo('/reports/messages_from_user')">{{ $t('Menu.TotalReceivedMessages') }}</div>
                <div v-if="!isChannel" class="nav-sub-item" @click="navigateTo('/reports/rejection')">{{ $t('Menu.Rejection') }}</div>
                <!-- Sources -->
                <div class="nav-sub-header">{{ $t('Menu.Sources') }}</div>
                <div class="nav-sub-item" @click="navigateTo('/reports/utm_summary')">{{ $t('Menu.UtmSummary') }}</div>
                <!-- Users -->
                <div class="nav-sub-header">{{ $t('Menu.Users') }}</div>
                <div class="nav-sub-item" @click="navigateTo('/reports/new_users')">{{ $t('Menu.NewUsers') }}</div>
                <div class="nav-sub-item" @click="navigateTo('/reports/left_users')">{{ $t('Menu.LeftUsers') }}</div>
                <div class="nav-sub-item" @click="navigateTo('/reports/return_users')">{{ $t('Menu.ReturnUsers') }}</div>
                <!-- App Sessions -->
                <template v-if="!isChannel">
                  <div class="nav-sub-header">{{ $t('Menu.AppSessionsTitle') }}</div>
                  <div class="nav-sub-item" @click="navigateTo('/reports/app_session_source')">{{ $t('Menu.AppSessionsSource') }}</div>
                  <div class="nav-sub-item" @click="navigateTo('/reports/app_session_device')">{{ $t('Menu.AppSessionsDevice') }}</div>
                  <div class="nav-sub-item" @click="navigateTo('/reports/app_session_version')">{{ $t('Menu.AppSessionsVersion') }}</div>
                </template>
                <!-- Sessions -->
                <template v-if="!isChannel">
                  <div class="nav-sub-header">{{ $t('Menu.SessionsTitle') }}</div>
                  <div class="nav-sub-item" @click="navigateTo('/reports/session_count')">{{ $t('Menu.Sessions') }}</div>
                  <div class="nav-sub-item" @click="navigateTo('/reports/avg_events_session')">{{ $t('Menu.AvgEventsSession') }}</div>
                  <div class="nav-sub-item" @click="navigateTo('/reports/avg_duration_session')">{{ $t('Menu.AvgDurationSession') }}</div>
                </template>
              </div>
            </transition>

            <!-- ── ИНСТРУМЕНТЫ ── -->
            <div class="nav-section-header">{{ $t('Menu.SectionTools') }}</div>

            <div class="nav-item" @click="navigateTo('/broadcasting')">
              <i class="fa-solid fa-paper-plane nav-icon"></i>
              <span>{{ $t('Menu.Broadcasting') }}</span>
            </div>

            <div class="nav-item" @click="navigateTo('/utm-builder')">
              <i class="fas fa-tools nav-icon"></i>
              <span>{{ $t('Menu.UTMBuilder') }}</span>
            </div>

            <div class="nav-item" @click="navigateTo('/workflows')">
              <i class="fas fa-project-diagram nav-icon"></i>
              <span>{{ $t('Menu.Workflows') }}</span>
            </div>

            <div class="nav-item" @click="navigateTo('/integrations')">
              <i class="fas fa-plug nav-icon"></i>
              <span>{{ $t('Menu.Integrations') }}</span>
            </div>

            <div v-if="$store.state.init.user && $store.state.init.user.admin" class="nav-item" @click="navigateTo('/assistant')">
              <i class="fas fa-robot nav-icon"></i>
              <span>{{ $t('Menu.Assistant') }}</span>
            </div>

            <!-- Referral (accordion) -->
            <div class="nav-item nav-item--accordion" @click="toggleAccordion('referral')">
              <i class="fas fa-gift nav-icon"></i>
              <span>{{ $t('Menu.Referral') }}</span>
              <i class="fas fa-chevron-down nav-caret" :class="{ 'nav-caret--open': openAccordions.referral }"></i>
            </div>
            <transition name="accordion">
              <div v-if="openAccordions.referral" class="nav-sub">
                <div class="nav-sub-item" @click="navigateTo('/referral')">{{ $t('Menu.ReferralSub.Overview') }}</div>
                <div class="nav-sub-item" @click="navigateTo('/referral/settings')">{{ $t('Menu.ReferralSub.Settings') }}</div>
                <div class="nav-sub-item" @click="navigateTo('/referral/referrers')">{{ $t('Menu.ReferralSub.Referrers') }}</div>
                <div class="nav-sub-item" @click="navigateTo('/referral/referrals')">{{ $t('Menu.ReferralSub.Referrals') }}</div>
                <div class="nav-sub-item" @click="navigateTo('/referral/accruals')">{{ $t('Menu.ReferralSub.Accruals') }}</div>
                <div class="nav-sub-item" @click="navigateTo('/referral/payouts')">{{ $t('Menu.ReferralSub.Payouts') }}</div>
              </div>
            </transition>

            <!-- ── НАСТРОЙКИ ── -->
            <div class="nav-section-header">{{ $t('Menu.SectionSettings') }}</div>

            <div class="nav-item" @click="navigateTo('/resorces')">
              <i class="fas fa-layer-group nav-icon"></i>
              <span>{{ $t('Menu.Bots') }}</span>
            </div>

            <div class="nav-item" @click="navigateTo('/projects')">
              <i class="fas fa-folder-tree nav-icon"></i>
              <span>{{ $t('Menu.Projects') }}</span>
            </div>

            <div class="nav-item" @click="navigateTo('/webapps')">
              <i class="fa-solid fa-mobile-screen-button nav-icon"></i>
              <span>{{ $t('Menu.Webapp') }}</span>
            </div>

            <div class="nav-item" @click="navigateTo('/code4-site')">
              <i class="fas fa-code nav-icon"></i>
              <span>{{ $t('Menu.CodeForSite') }}</span>
            </div>

            <div class="nav-item" @click="navigateTo('/custom-fields')">
              <i class="fas fa-tags nav-icon"></i>
              <span>{{ $t('Menu.CustomFields') }}</span>
            </div>

            <div class="nav-item" @click="navigateTo('/landings')">
              <i class="fas fa-link nav-icon"></i>
              <span>{{ $t('Menu.Landings') }}</span>
            </div>

            <div class="nav-item" @click="navigateTo('/bot_errors')">
              <i class="fas fa-bug nav-icon"></i>
              <span>{{ $t('Menu.BotErrors') }}</span>
            </div>

            <!-- ── АДМИНКА ── -->
            <template v-if="$store.state.init.user && $store.state.init.user.admin">
              <div class="nav-section-header">АДМИНКА</div>

              <div class="nav-item" @click="navigateTo('/admin/users')">
                <i class="fas fa-users-cog nav-icon"></i>
                <span>Пользователи</span>
              </div>

              <div class="nav-item" @click="navigateTo('/admin/bots')">
                <i class="fas fa-robot nav-icon"></i>
                <span>Боты</span>
              </div>

              <div class="nav-item" @click="navigateTo('/admin/billing')">
                <i class="fas fa-money-bill-wave nav-icon"></i>
                <span>Биллинг</span>
              </div>

              <div class="nav-item" @click="navigateTo('/admin/usage')">
                <i class="fas fa-chart-pie nav-icon"></i>
                <span>Потребление</span>
              </div>

              <div class="nav-item" @click="navigateTo('/admin/payments')">
                <i class="fas fa-file-invoice-dollar nav-icon"></i>
                <span>Платежи</span>
              </div>

              <div class="nav-item" @click="navigateTo('/admin/workflow-templates')">
                <i class="fas fa-th-large nav-icon"></i>
                <span>Каталог автоматизаций</span>
              </div>

              <div class="nav-item" @click="navigateTo('/admin/tags')">
                <i class="fas fa-tags nav-icon"></i>
                <span>{{ $t('AdminTags.title') }}</span>
              </div>
            </template>

          </nav>

          <sidenav-announcement />

          <div class="mobile-sidebar__feedback-wrap">
            <button class="mobile-sidebar__feedback-btn" @click="feedbackOpen = true">
              <i class="fas fa-comment-dots"></i>
              <span>{{ $t('Feedback.ButtonShort') }}</span>
            </button>
          </div>
        </aside>

        <!-- ── Reports sub-panel ── -->
        <transition name="reports-subpanel">
          <aside v-if="reportsPanelOpen" class="mobile-reports-panel" @click.stop @touchmove.stop @wheel.stop>
            <!-- Header -->
            <div class="rp-header">
              <button class="rp-back" @click="closeReportsPanel">
                <i class="fas fa-chevron-left"></i>
              </button>
              <span class="rp-title">{{ $t('Menu.Reports') }}</span>
            </div>

            <!-- Search -->
            <div class="rp-search-row">
              <i class="fas fa-search rp-search-icon"></i>
              <input
                v-model="reportsSearchQuery"
                class="rp-search-input"
                :placeholder="$t('Menu.ReportsSearch')"
              />
            </div>

            <!-- Categories -->
            <div class="rp-cats">
              <div
                v-for="cat in reportCategories"
                :key="cat.key"
                class="rp-cat-btn"
                :class="{ active: reportsSelectedCategory === cat.key }"
                @click="reportsSelectedCategory = cat.key; reportsSearchQuery = ''"
              >
                <i :class="'fas ' + cat.icon"></i>
                <span>{{ $t(cat.labelKey) }}</span>
                <span v-if="cat.count > 0" class="rp-cat-count">{{ cat.count }}</span>
              </div>
            </div>

            <div class="mobile-sidebar__divider" style="margin:0 12px 4px;"></div>

            <!-- Quick actions -->
            <div class="rp-action-item" @click="navigateTo('/user-reports')">
              <span class="rp-action-icon-wrap"><i class="fas fa-list"></i></span>
              <span>{{ $t('Menu.ReportsList') }}</span>
            </div>
            <div class="rp-action-item" @click="navigateTo('/report-builder')">
              <span class="rp-action-icon-wrap"><i class="fas fa-plus"></i></span>
              <span>{{ $t('Menu.CreateReport') }}</span>
            </div>

            <div class="mobile-sidebar__divider" style="margin:4px 12px 4px;"></div>

            <!-- Reports list -->
            <div class="rp-list">
              <!-- Loading -->
              <div v-if="userReportsLoading && reportsSelectedCategory !== 'fixed'" class="rp-state-msg">
                <i class="fas fa-circle-notch fa-spin me-1"></i>
              </div>

              <!-- User reports -->
              <template v-else-if="reportsSelectedCategory !== 'fixed'">
                <div
                  v-for="r in visibleUserReports"
                  :key="r.uuid"
                  class="rp-item"
                  @click="navigateTo('/user-reports/view/' + r.uuid)"
                >
                  <span class="rp-item-icon-wrap">
                    <i :class="'fas ' + getTypeIcon(r.type)"></i>
                  </span>
                  <span class="rp-item-text">
                    <span class="rp-item-name-row">
                      <span class="rp-item-name">{{ getReportName(r) }}</span>
                      <i
                        v-if="r.is_system === 1 && r.is_free !== 1"
                        class="fas fa-crown rp-premium-badge"
                        :title="$t('UserReports.badges.premiumTooltip')"
                      ></i>
                    </span>
                    <span class="rp-item-desc">{{ getTypeName(r.type) }}</span>
                  </span>
                </div>
                <div v-if="visibleUserReports.length === 0" class="rp-state-msg">
                  {{ reportsSearchQuery ? $t('Menu.ReportsNoResults') : $t('UserReports.hint') }}
                </div>
              </template>

              <!-- Fixed reports: shown in 'fixed' tab or included in 'all' view -->
              <template v-if="reportsSelectedCategory === 'fixed' || reportsSelectedCategory === 'all'">
                <div
                  v-for="r in visibleFixedReports"
                  :key="r.route"
                  class="rp-item"
                  @click="navigateTo(r.route)"
                >
                  <span class="rp-item-icon-wrap">
                    <i :class="'fas ' + r.icon"></i>
                  </span>
                  <span class="rp-item-text">
                    <span class="rp-item-name">{{ $t(r.nameKey) }}</span>
                  </span>
                </div>
                <div v-if="visibleFixedReports.length === 0 && reportsSelectedCategory === 'fixed'" class="rp-state-msg">
                  {{ $t('Menu.ReportsNoResults') }}
                </div>
              </template>
            </div>
          </aside>
        </transition>

      </div>
    </transition>

    <FeedbackModal v-if="feedbackOpen" source="mobile_sidebar" @close="feedbackOpen = false" />
  </teleport>
</template>

<script>
import userReportService from '@/helpers/api/userReport.service'
import { ElMessage } from "element-plus";
import noAvatarImg from "@/assets/img/chat/no_avatar.png";
import FeedbackModal from "@/components/FeedbackModal.vue";
import SidenavAnnouncement from "@/examples/Sidenav/SidenavAnnouncement.vue";

const USER_REPORTS_CACHE_KEY = 'sidebar_user_reports_cache';
const USER_REPORTS_CACHE_TTL = 5 * 60 * 1000;

export default {
  name: 'MobileSidebar',
  components: { FeedbackModal, SidenavAnnouncement },
  data() {
    return {
      noAvatarImg,
      botDropdownOpen: false,
      openAccordions: {
        conversions: false,
        reports_old: false,
        referral: false,
      },
      reportsPanelOpen: false,
      userReports: [],
      userReportsLoading: false,
      reportsSearchQuery: '',
      reportsSelectedCategory: 'all',
      feedbackOpen: false,
    };
  },
  computed: {
    isOpen() {
      return this.$store.state.theme.mobileSidebarOpen;
    },
    currentBot() {
      return this.$store.state.init.current_bot;
    },
    userBots() {
      return this.$store.state.init.user_bots;
    },
    hasBots() {
      return !this.$store.getters['init/getHasDemobot'] && Object.keys(this.userBots).length;
    },
    isChannel() {
      return this.$store?.state?.init?.current_bot?.type === 2;
    },
    isFreeTariff() {
      const bot = this.$store?.state?.init?.current_bot;
      return !bot || !bot.tariff;
    },
    show2FAPromo() {
      const user = this.$store.state.init.user;
      return user && user.has_2fa === false;
    },
    reportCategories() {
      const types = ['trends', 'list', 'funnels', 'retention'];
      const iconMap = { trends: 'fa-chart-line', list: 'fa-table', funnels: 'fa-filter', retention: 'fa-redo-alt' };
      const labelMap = { trends: 'Menu.TypeTrends', list: 'Menu.TypeTable', funnels: 'Menu.TypeFunnel', retention: 'Menu.TypeRetention' };
      return [
        { key: 'all',   icon: 'fa-th-large', labelKey: 'Menu.AllReports',   count: 0 },
        { key: 'mine',  icon: 'fa-user',     labelKey: 'Menu.MyReports',    count: this.userReports.filter(r => !r.is_system).length },
        ...types.map(t => ({
          key: t,
          icon: iconMap[t],
          labelKey: labelMap[t],
          count: this.userReports.filter(r => r.type === t).length,
        })),
        { key: 'fixed', icon: 'fa-archive',  labelKey: 'Menu.FixedReports', count: 0 },
      ];
    },
    allFixedReports() {
      const reports = [];
      if (!this.isChannel) {
        reports.push(
          { route: '/reports/active_users?details=month', icon: 'fa-users',       nameKey: 'Menu.ActiveUsersMAU' },
          { route: '/reports/active_users?details=week',  icon: 'fa-users',       nameKey: 'Menu.ActiveUsersWAU' },
          { route: '/reports/active_users?details=day',   icon: 'fa-users',       nameKey: 'Menu.ActiveUsersDAU' },
        );
      }
      reports.push({ route: '/reports/messages_from_user', icon: 'fa-comments',    nameKey: 'Menu.TotalReceivedMessages' });
      if (!this.isChannel) {
        reports.push({ route: '/reports/rejection',         icon: 'fa-door-open',  nameKey: 'Menu.Rejection' });
      }
      reports.push(
        { route: '/reports/utm_summary',          icon: 'fa-link',       nameKey: 'Menu.UtmSummary' },
        { route: '/reports/new_users',            icon: 'fa-user-plus',  nameKey: 'Menu.NewUsers' },
        { route: '/reports/left_users',           icon: 'fa-user-minus', nameKey: 'Menu.LeftUsers' },
        { route: '/reports/return_users',         icon: 'fa-user-clock', nameKey: 'Menu.ReturnUsers' },
      );
      if (!this.isChannel) {
        reports.push(
          { route: '/reports/app_session_source',   icon: 'fa-mobile-alt',  nameKey: 'Menu.AppSessionsSource' },
          { route: '/reports/app_session_device',   icon: 'fa-tablet-alt',  nameKey: 'Menu.AppSessionsDevice' },
          { route: '/reports/app_session_version',  icon: 'fa-code-branch', nameKey: 'Menu.AppSessionsVersion' },
          { route: '/reports/session_count',        icon: 'fa-layer-group', nameKey: 'Menu.Sessions' },
          { route: '/reports/avg_events_session',   icon: 'fa-bolt',        nameKey: 'Menu.AvgEventsSession' },
          { route: '/reports/avg_duration_session', icon: 'fa-stopwatch',   nameKey: 'Menu.AvgDurationSession' },
        );
      }
      return reports;
    },
    visibleFixedReports() {
      const q = this.reportsSearchQuery.trim().toLowerCase();
      if (!q) return this.allFixedReports;
      return this.allFixedReports.filter(r => this.$t(r.nameKey).toLowerCase().includes(q));
    },
    visibleUserReports() {
      let list = this.userReports;
      if (this.reportsSelectedCategory === 'mine') {
        list = list.filter(r => !r.is_system);
      } else if (this.reportsSelectedCategory !== 'all') {
        list = list.filter(r => r.type === this.reportsSelectedCategory);
      }
      const q = this.reportsSearchQuery.trim().toLowerCase();
      if (q) list = list.filter(r => this.getReportName(r).toLowerCase().includes(q));
      return list;
    },
  },
  watch: {
    isOpen(val) {
      document.body.style.overflow = val ? 'hidden' : '';
      if (!val) this.reportsPanelOpen = false;
    },
    '$store.state.theme.mobileReportsPanelRequest'(val) {
      if (val) {
        this.openReportsPanel();
        this.$store.commit('theme/clearMobileReportsPanelRequest');
      }
    }
  },
  mounted() {
    this.loadUserReports();
  },
  methods: {
    goTo2FA() {
      ElMessage({ message: this.$t('Navbar.Protect2FAMessage'), type: 'warning', duration: 4000 });
      this.navigateTo('/profile');
    },
    close() {
      this.$store.commit('theme/closeMobileSidebar');
      this.botDropdownOpen = false;
      this.reportsPanelOpen = false;
    },
    navigateTo(path) {
      this.$router.push(path);
      this.close();
    },
    navigateToNamed(name) {
      this.$router.push({ name });
      this.close();
    },
    switchBot(bot) {
      this.$store.commit('init/setCurrentBot', bot);
      this.botDropdownOpen = false;
    },
    toggleAccordion(key) {
      this.openAccordions[key] = !this.openAccordions[key];
    },
    openReportsPanel() {
      this.reportsSearchQuery = '';
      this.reportsSelectedCategory = 'all';
      this.reportsPanelOpen = true;
    },
    closeReportsPanel() {
      this.reportsPanelOpen = false;
    },
    async loadUserReports() {
      try {
        const raw = localStorage.getItem(USER_REPORTS_CACHE_KEY);
        if (raw) {
          const { data, ts } = JSON.parse(raw);
          if (Date.now() - ts < USER_REPORTS_CACHE_TTL) {
            this.userReports = data;
            return;
          }
        }
      } catch (_) { /* ignore */ }
      this.userReportsLoading = true;
      try {
        const data = await userReportService.index();
        this.userReports = Array.isArray(data) ? data : [];
        try {
          localStorage.setItem(USER_REPORTS_CACHE_KEY, JSON.stringify({ data: this.userReports, ts: Date.now() }));
        } catch (_) { /* quota */ }
      } catch (_) {
        this.userReports = [];
      } finally {
        this.userReportsLoading = false;
      }
    },
    getReportName(report) {
      const locale = this.$i18n?.locale || 'ru';
      return report.translations?.[locale]?.name
        ?? report.translations?.['en']?.name
        ?? report.name
        ?? '—';
    },
    getTypeIcon(type) {
      const map = { trends: 'fa-chart-line', list: 'fa-table', funnel: 'fa-filter', retention: 'fa-redo-alt' };
      return map[type] || 'fa-chart-bar';
    },
    getTypeName(type) {
      const map = {
        trends: this.$t('Menu.TypeTrends'),
        list: this.$t('Menu.TypeTable'),
        funnel: this.$t('Menu.TypeFunnel'),
        retention: this.$t('Menu.TypeRetention'),
      };
      return map[type] || type || '';
    },
  },
  beforeUnmount() {
    document.body.style.overflow = '';
  }
};
</script>

<style scoped>
/* ===== Overlay ===== */
.mobile-sidebar-overlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
}

/* ===== Main sidebar panel ===== */
.mobile-sidebar {
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  width: 85%;
  max-width: 320px;
  background: #fff;
  box-shadow: 4px 0 24px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

/* ===== Reports sub-panel ===== */
.mobile-reports-panel {
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  width: 85%;
  max-width: 320px;
  background: #fff;
  box-shadow: 4px 0 24px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  z-index: 10;
}

/* ===== Transitions ===== */
.mobile-sidebar-enter-active,
.mobile-sidebar-leave-active {
  transition: opacity 0.28s ease;
}
.mobile-sidebar-enter-active .mobile-sidebar,
.mobile-sidebar-leave-active .mobile-sidebar {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
.mobile-sidebar-enter-from,
.mobile-sidebar-leave-to {
  opacity: 0;
}
.mobile-sidebar-enter-from .mobile-sidebar,
.mobile-sidebar-leave-to .mobile-sidebar {
  transform: translateX(-100%);
}

/* Sub-panel slides in from the right */
.reports-subpanel-enter-active,
.reports-subpanel-leave-active {
  transition: transform 0.28s cubic-bezier(0.4, 0, 0.2, 1);
}
.reports-subpanel-enter-from,
.reports-subpanel-leave-to {
  transform: translateX(100%);
}

/* ===== Bot selector ===== */
.mobile-sidebar__bot-selector {
  padding: 16px 16px 8px;
}
.bot-selector-current {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 10px;
  background: #f5f7fa;
  cursor: pointer;
  transition: background 0.15s;
}
.bot-selector-current:active { background: #e8ecf1; }
.bot-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  object-fit: cover;
  background: #e0e0e0;
}
.bot-avatar--sm { width: 24px; height: 24px; }
.bot-name {
  font-weight: 600;
  font-size: 14px;
  color: #2d3748;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
}
.bot-caret {
  font-size: 10px;
  color: #a0aec0;
  transition: transform 0.2s;
  margin-left: auto;
}
.bot-caret--open { transform: rotate(180deg); }
.bot-dropdown {
  margin-top: 4px;
  border-radius: 8px;
  background: #f5f7fa;
  overflow: hidden;
}
.bot-dropdown__item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  font-size: 13px;
  color: #4a5568;
  cursor: pointer;
  transition: background 0.12s;
}
.bot-dropdown__item:active { background: #e2e8f0; }
.bot-dropdown-enter-active,
.bot-dropdown-leave-active {
  transition: opacity 0.15s, max-height 0.2s ease;
  max-height: 300px;
  overflow: hidden;
}
.bot-dropdown-enter-from,
.bot-dropdown-leave-to { opacity: 0; max-height: 0; }

/* ===== Divider ===== */
.mobile-sidebar__divider {
  height: 1px;
  background: #e2e8f0;
  margin: 4px 16px 8px;
}

/* ===== 2FA promo ===== */
.mobile-sidebar__2fa-promo {
  margin: 0 12px 8px;
  padding: 10px 14px;
  border-radius: 8px;
  background: rgba(251, 140, 0, 0.1);
  border: 1px solid rgba(251, 140, 0, 0.35);
  color: #e65100;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  transition: background 0.15s;
}
.mobile-sidebar__2fa-promo:active {
  background: rgba(251, 140, 0, 0.18);
}

/* ===== Nav ===== */
.mobile-sidebar__nav {
  padding: 0 8px 80px;
  flex: 1;
}
.nav-section-header {
  padding: 14px 12px 4px;
  font-size: 10px;
  font-weight: 700;
  color: #a0aec0;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}
.nav-section-header:first-child {
  padding-top: 6px;
}
.nav-item {
  display: flex;
  align-items: center;
  gap: 10px;
  height: 44px;
  padding: 0 12px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #2d3748;
  cursor: pointer;
  transition: background 0.12s;
  -webkit-tap-highlight-color: transparent;
  user-select: none;
}
.nav-item:active { background: #f0faf4; }
.nav-icon {
  width: 20px;
  text-align: center;
  font-size: 15px;
  color: #718096;
  flex-shrink: 0;
}
.nav-item--accordion,
.nav-item--has-panel { position: relative; }
.nav-caret {
  font-size: 10px;
  color: #a0aec0;
  margin-left: auto;
  transition: transform 0.2s;
}
.nav-caret--open { transform: rotate(180deg); }
.nav-item--premium {
  background-color: rgba(255, 193, 7, 0.12);
}
.nav-item--premium:active {
  background-color: rgba(255, 193, 7, 0.2);
}
.nav-arrow {
  font-size: 10px;
  color: #a0aec0;
  margin-left: auto;
}

/* Sub-items */
.nav-sub { overflow: hidden; }
.nav-sub-header {
  padding: 8px 12px 4px 42px;
  font-size: 11px;
  font-weight: 700;
  color: #a0aec0;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.nav-sub-item {
  display: flex;
  align-items: center;
  height: 38px;
  padding: 0 12px 0 42px;
  border-radius: 6px;
  font-size: 13px;
  color: #4a5568;
  cursor: pointer;
  transition: background 0.12s;
  -webkit-tap-highlight-color: transparent;
}
.nav-sub-item:active { background: #f0faf4; color: #2dce89; }

/* Accordion transition */
.accordion-enter-active,
.accordion-leave-active {
  transition: all 0.25s ease;
  max-height: 800px;
  overflow: hidden;
}
.accordion-enter-from,
.accordion-leave-to { max-height: 0; opacity: 0; }

/* ===== Reports sub-panel content ===== */
.rp-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px 10px;
  border-bottom: 1px solid #e2e8f0;
  flex-shrink: 0;
}
.rp-back {
  background: none;
  border: none;
  padding: 4px 8px 4px 0;
  cursor: pointer;
  color: #5e72e4;
  font-size: 14px;
  line-height: 1;
  -webkit-tap-highlight-color: transparent;
}
.rp-title {
  font-size: 15px;
  font-weight: 700;
  color: #2d3748;
}

.rp-search-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 10px 12px 6px;
  padding: 7px 10px;
  border-radius: 8px;
  background: #f5f7fa;
  border: 1px solid #e2e8f0;
  flex-shrink: 0;
}
.rp-search-icon { color: #a0aec0; font-size: 12px; flex-shrink: 0; }
.rp-search-input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 13px;
  color: #2d3748;
  min-width: 0;
}
.rp-search-input::placeholder { color: #a0aec0; }

/* Categories */
.rp-cats {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 4px 12px 8px;
  flex-shrink: 0;
}
.rp-cat-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 9px;
  border-radius: 6px;
  font-size: 12px;
  color: #718096;
  background: #f5f7fa;
  cursor: pointer;
  transition: background 0.12s, color 0.12s;
  -webkit-tap-highlight-color: transparent;
  user-select: none;
}
.rp-cat-btn.active {
  background: rgba(94, 114, 228, 0.12);
  color: #5e72e4;
  font-weight: 600;
}
.rp-cat-count {
  font-size: 10px;
  background: rgba(94, 114, 228, 0.15);
  color: #5e72e4;
  border-radius: 8px;
  padding: 0 5px;
  font-weight: 700;
}

/* Quick actions */
.rp-action-item {
  display: flex;
  align-items: center;
  gap: 10px;
  height: 40px;
  padding: 0 16px;
  font-size: 13px;
  font-weight: 600;
  color: #5e72e4;
  cursor: pointer;
  transition: background 0.12s;
  -webkit-tap-highlight-color: transparent;
  flex-shrink: 0;
}
.rp-action-item:active { background: rgba(94, 114, 228, 0.06); }
.rp-action-icon-wrap {
  width: 22px;
  text-align: center;
  font-size: 12px;
}

/* List */
.rp-list {
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 4px 8px 24px;
}
.rp-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.12s;
  -webkit-tap-highlight-color: transparent;
}
.rp-item:active { background: #f0faf4; }
.rp-item-icon-wrap {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: rgba(94, 114, 228, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #5e72e4;
  font-size: 11px;
}
.rp-item-text { display: flex; flex-direction: column; min-width: 0; }
.rp-item-name-row { display: flex; align-items: center; gap: 5px; min-width: 0; }
.rp-item-name {
  font-size: 13px;
  font-weight: 500;
  color: #2d3748;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.rp-premium-badge { flex-shrink: 0; font-size: 10px; color: #B85A0B; }
:root[data-theme="dark"] .rp-premium-badge,
.dark-version .rp-premium-badge { color: #fbbf4b; }
.rp-item-desc {
  font-size: 11px;
  color: #8392ab;
  margin-top: 1px;
}
.rp-state-msg {
  text-align: center;
  padding: 24px 12px;
  color: #a0aec0;
  font-size: 12px;
}

/* ===== Feedback button ===== */
.mobile-sidebar__feedback-wrap {
  flex-shrink: 0;
  padding: 8px 16px 16px;
}
.mobile-sidebar__feedback-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  height: 44px;
  border: none;
  border-radius: 10px;
  background: var(--el-color-primary, #5e72e4);
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(94, 114, 228, 0.35);
  -webkit-tap-highlight-color: transparent;
}
.mobile-sidebar__feedback-btn:active {
  filter: brightness(1.08);
}

/* Only render on mobile */
@media (min-width: 1200px) {
  .mobile-sidebar-overlay { display: none !important; }
}

/* ===== Dark mode ===== */
:root[data-theme="dark"] .mobile-sidebar,
:root[data-theme="dark"] .mobile-reports-panel,
.dark-version .mobile-sidebar,
.dark-version .mobile-reports-panel {
  background: #1a1d2e;
}
:root[data-theme="dark"] .rp-header,
.dark-version .rp-header {
  border-bottom-color: rgba(255, 255, 255, 0.08);
}
:root[data-theme="dark"] .rp-title,
.dark-version .rp-title { color: #e2e8f0; }
:root[data-theme="dark"] .rp-search-row,
.dark-version .rp-search-row {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.08);
}
:root[data-theme="dark"] .rp-search-input,
.dark-version .rp-search-input { color: #e2e8f0; }
:root[data-theme="dark"] .rp-cat-btn,
.dark-version .rp-cat-btn { background: rgba(255, 255, 255, 0.05); color: #a0aec0; }
:root[data-theme="dark"] .rp-cat-btn.active,
.dark-version .rp-cat-btn.active { background: rgba(130, 94, 228, 0.2); color: #a78bfa; }
:root[data-theme="dark"] .rp-item-name,
.dark-version .rp-item-name { color: #e0e0f0; }
:root[data-theme="dark"] .rp-item-icon-wrap,
.dark-version .rp-item-icon-wrap { background: rgba(130, 94, 228, 0.18); color: #a78bfa; }
:root[data-theme="dark"] .bot-selector-current,
.dark-version .bot-selector-current { background: rgba(255, 255, 255, 0.06); }
:root[data-theme="dark"] .bot-name,
.dark-version .bot-name { color: #e2e8f0; }
:root[data-theme="dark"] .bot-dropdown,
.dark-version .bot-dropdown { background: rgba(255, 255, 255, 0.06); }
:root[data-theme="dark"] .bot-dropdown__item,
.dark-version .bot-dropdown__item { color: #cbd5e0; }
:root[data-theme="dark"] .mobile-sidebar__divider,
.dark-version .mobile-sidebar__divider { background: rgba(255, 255, 255, 0.08); }
:root[data-theme="dark"] .nav-section-header,
.dark-version .nav-section-header { color: #4a5568; }
:root[data-theme="dark"] .nav-item,
.dark-version .nav-item { color: #e2e8f0; }
:root[data-theme="dark"] .nav-item:active,
.dark-version .nav-item:active { background: rgba(45, 206, 137, 0.1); }
:root[data-theme="dark"] .nav-icon,
.dark-version .nav-icon { color: #a0aec0; }
:root[data-theme="dark"] .nav-sub-header,
.dark-version .nav-sub-header { color: #718096; }
:root[data-theme="dark"] .nav-sub-item,
.dark-version .nav-sub-item { color: #cbd5e0; }
:root[data-theme="dark"] .nav-sub-item:active,
.dark-version .nav-sub-item:active { background: rgba(45, 206, 137, 0.1); color: #2dce89; }
</style>
